/**
 * The authorization-code-with-PKCE half of sign-in, as pure functions.
 *
 * A browser cannot keep a client secret, so the app proves it is the same
 * caller that started the flow by holding a random verifier and sending only
 * its hash to Cognito up front.
 */

/** Written into the page by the deploy; see the CDK admin-site construct. */
export interface SiteConfig {
  userPoolId: string;
  clientId: string;
  /** `https://<prefix>.auth.<region>.amazoncognito.com`, no trailing slash. */
  cognitoDomain: string;
  apiUrl: string;
}

function field(raw: Record<string, unknown>, name: string): string {
  const value = raw[name];
  if (typeof value !== "string" || value === "") {
    throw new Error(`config.json is missing ${name}`);
  }
  return value;
}

export function parseConfig(value: unknown): SiteConfig {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("config.json is not an object");
  }
  const raw = value as Record<string, unknown>;
  return {
    userPoolId: field(raw, "userPoolId"),
    clientId: field(raw, "clientId"),
    cognitoDomain: field(raw, "cognitoDomain").replace(/\/$/, ""),
    apiUrl: field(raw, "apiUrl"),
  };
}

function base64url(bytes: Uint8Array): string {
  let text = "";
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * 32 random bytes, 43 characters once base64url-encoded. Used for both the
 * PKCE verifier and the state value, which need the same thing of it:
 * unguessable, and safe in a URL.
 */
export function randomToken(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)));
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64url(new Uint8Array(digest));
}

/**
 * Straight to Google. Cognito's own page would only offer the same one button,
 * because Google is the pool's only identity provider.
 */
export function authorizeUrl(
  config: SiteConfig,
  options: { redirectUri: string; challenge: string; state: string },
): string {
  const query = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: options.redirectUri,
    scope: "openid email profile",
    identity_provider: "Google",
    code_challenge_method: "S256",
    code_challenge: options.challenge,
    state: options.state,
  });
  return `${config.cognitoDomain}/oauth2/authorize?${query}`;
}

export function logoutUrl(config: SiteConfig, redirectUri: string): string {
  const query = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: redirectUri,
  });
  return `${config.cognitoDomain}/logout?${query}`;
}

export function tokenUrl(config: SiteConfig): string {
  return `${config.cognitoDomain}/oauth2/token`;
}

export function codeExchangeBody(
  config: SiteConfig,
  options: { code: string; verifier: string; redirectUri: string },
): URLSearchParams {
  return new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    code: options.code,
    code_verifier: options.verifier,
    redirect_uri: options.redirectUri,
  });
}

export function refreshBody(config: SiteConfig, refreshToken: string): URLSearchParams {
  return new URLSearchParams({
    grant_type: "refresh_token",
    client_id: config.clientId,
    refresh_token: refreshToken,
  });
}

export interface Tokens {
  idToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export function parseTokenResponse(value: unknown, now: number): Tokens {
  if (typeof value !== "object" || value === null) {
    throw new Error("token response is not an object");
  }
  const raw = value as Record<string, unknown>;
  if (typeof raw.id_token !== "string" || raw.id_token === "") {
    throw new Error("token response carries no id_token");
  }
  const expiresIn = typeof raw.expires_in === "number" ? raw.expires_in : 3600;
  return {
    idToken: raw.id_token,
    ...(typeof raw.refresh_token === "string"
      ? { refreshToken: raw.refresh_token }
      : {}),
    expiresAt: now + expiresIn * 1000,
  };
}

/** Refreshed a minute early, so a request never leaves with a dead token. */
export function isExpired(tokens: Tokens, now: number): boolean {
  return tokens.expiresAt - 60_000 <= now;
}
